import { displayMarketKey, isEnrichablePlayerProp, getKnownMarketIds, isTeamPickMarket, isBinaryMarket, resolveMarket } from './markets';
import { isNegativeLine } from './betting';
import { splitSignedPick, resolveSpreadLine, numericLineOrNull } from './signed-line';
import { SIGNED_LINE_MARKETS, hasSpreadSignFlag, isMarketEditFlag, spreadSignState } from './spread-sign';

// #1166 D2.4 (fix round 1): per-reason copy for a leg with no gameId. Single owner here --
// ImportConfirmDetails.jsx and SlipImporter.jsx import gamelessLegMessage() for their per-row
// text instead of duplicating the ternary, so the three strings can't drift from rule 2c below.
// DESIGN.md "Don't use em dashes in UI copy": these use a period, not an em dash.
export const GAMELESS_LEG_MESSAGES = {
  ambiguous_game: 'Pick which game this bet is on.',
  cache_miss: "We couldn't match this game yet. Try again closer to game time.",
  default: "We couldn't read the teams on this leg. Try a clearer screenshot.",
};

export function gamelessLegMessage(reason) {
  // Pre-Codex r2 SEC2-inv: own entries only -- '__proto__' / 'constructor' are not reasons.
  return (reason != null && Object.hasOwn(GAMELESS_LEG_MESSAGES, reason) && GAMELESS_LEG_MESSAGES[reason]) || GAMELESS_LEG_MESSAGES.default;
}

// #1132 D5.2: spread-sign copy. Single owner -- the confirm rows and the hard-block list
// both read spreadSignMessage(). `{target}` is "the <pick> spread line" or "this spread
// line". No em dashes (DESIGN.md).
export const SPREAD_SIGN_MESSAGES = {
  pick_suffix: 'Remove the line from the team name, then set it in the line box.',
  unconfirmed: 'Set the sign on {target}: - for the favourite, + for the underdog.',
  mismatch: "The sign on {target} doesn't match the line. Set it again.",
  missing_line: 'Enter {target} with its sign, for example -3.5.',
};
// Shown when import-confirm answers 422 SPREAD_SIGN_UNCONFIRMED (a flag the client did not predict).
export const SPREAD_SIGN_UNCONFIRMED_422_MESSAGE = 'Check the sign on each spread line, then save again.';

// Spec 2026-09-25 §8: client-owned copy for the server's field-authority 422 refusals.
export const UNKNOWN_SPORTSBOOK_422_MESSAGE = "That sportsbook isn't supported.";
export const NEGATIVE_LINE_MESSAGE = "A total or over/under line can't be negative — fix the line before saving.";
// #935: import-confirm's per-user daily cap. 429 = cap reached; 503 = the cap check was
// briefly unavailable (fail-closed, retriable). Neither loses the parsed slip.
export const SAVE_DAILY_LIMIT_MESSAGE = "You've hit today's save limit. Try again tomorrow.";
export const SAVE_LIMIT_UNAVAILABLE_MESSAGE = "Saving is briefly unavailable. Try again in a minute.";

export function spreadSignMessage(leg, state) {
  if (splitSignedPick(leg && leg.pick)) return SPREAD_SIGN_MESSAGES.pick_suffix;
  const pick = leg && typeof leg.pick === 'string' && leg.pick.trim() ? leg.pick.trim() : null;
  const target = pick ? `the ${pick} spread line` : 'this spread line';
  return (SPREAD_SIGN_MESSAGES[state] || SPREAD_SIGN_MESSAGES.unconfirmed).replace('{target}', target);
}

// The line import-confirm's storedLineFor will write for this leg (#1132 D1.1: same code).
export function effectiveSpreadLine(leg) {
  return numericLineOrNull(resolveSpreadLine(leg.pick, leg.line));
}

// A superset of every way the server stores a market (#1132 D5.1): NHL
// canonicalMarket ?? market ?? type, NFL resolveMarket with NO sportsbook, and MLB, where a
// settleable spread keeps its raw label.
function clientSigned(leg, league) {
  return [leg.canonicalMarket, leg.market, leg.type, resolveMarket(leg.market ?? leg.type, league)]
    .some((m) => m != null && SIGNED_LINE_MARKETS.has(m));
}

/**
 * Compute save blockers for the import confirm screen.
 * Returns:
 *   canSave: boolean           — false only on hard stops
 *   hardBlockMessages: string[] — hard stops (button disabled)
 *   softWarnMessages: string[]  — soft stops (button enabled, inline warning)
 *   affectedLegIndices: number[] — leg indices with a per-leg issue (hard or soft)
 *   messages: string[]          — backward compat alias for hardBlockMessages
 *   spreadSignLegIndices: number[] — #1132: legs that get the spread sign control
 *   legMessages: {[idx]: string}   — #1132: the per-row note for a blocked spread leg
 *
 * options (#1132 D5.1):
 *   originalParsed    — the vision response as returned (market-edit source)
 *   failedLegIndexes  — the last 422 SPREAD_SIGN_UNCONFIRMED response's indexes
 */
export function computeSaveBlockers(parsed, { originalParsed = null, failedLegIndexes = null } = {}) {
  const hardBlockMessages = [];
  const softWarnMessages = [];
  const affectedLegIndexSet = new Set();
  const spreadSignLegIndices = [];
  const legMessages = {};

  // ── HARD STOPS (save button disabled) ──────────────────────────────

  // 1. No legs detected
  if (!parsed.legs || parsed.legs.length === 0) {
    // #1688: no manual entry since #166 -- offer what exists (SlipSync books per #1672).
    hardBlockMessages.push('No legs detected — try another screenshot, or sync DraftKings or BetMGM bets with SlipSync.');
  }

  // 2. Missing or unknown sportsbook
  if (!parsed.sportsbook || parsed.sportsbook === 'unknown') {
    hardBlockMessages.push('Select a sportsbook before saving.');
  }

  // 2b. #1135: a negative line on a total / O/U prop. The line is a count, so it can never
  // be negative; settlement grades it unresolvable (betting.js isNegativeLine) and SlipSync
  // rejects it. HARD, not soft: the confirm screen is where a user edit introduces it, and
  // saving it only produces a bet that can never grade. Keyed on a market the registry
  // positively KNOWS is not a team pick -- an unrecognised label (e.g. a raw "Run Line")
  // might be a signed spread, so it is never hard-blocked here. Binary props (anytime_*)
  // are excluded too: the parlay/SGP confirm view shows no line field for them, so a hard
  // stop would be a dead end (pre-Codex r2 F1); settlement fails them closed regardless.
  (parsed.legs || []).forEach((l) => {
    const market = l.canonicalMarket || l.type || l.market;
    const league = l.league ?? parsed.league ?? 'nhl';
    if (!market || !getKnownMarketIds(league).has(market) || isTeamPickMarket(market, league)) return;
    if (isBinaryMarket(market, league)) return;
    if (!isNegativeLine(l.line)) return;
    const msg = NEGATIVE_LINE_MESSAGE;
    if (!hardBlockMessages.includes(msg)) hardBlockMessages.push(msg);
  });

  // 2c. #1166 D2.4: a leg with no gameId can never save -- import-confirm's #907 gate
  // rejects the whole import (422). Hard stop, truthful copy per reason (see
  // gamelessLegMessage() above -- the single owner of this copy).
  (parsed.legs || []).forEach((l, idx) => {
    if (l && l.gameId) return;
    const reason = l && l.resolution_failure_reason;
    const msg = gamelessLegMessage(reason);
    if (!hardBlockMessages.includes(msg)) hardBlockMessages.push(msg);
    affectedLegIndexSet.add(idx);
  });

  // 2d. #1132 D5.1: a spread leg whose sign the parse could not establish saves only with
  // a stored line <= 0 or an explicit "+" (spread-sign.js). HARD: a dropped minus grades
  // a favourite as the underdog, terminally. Replaces the #1040 PR D0 soft stop 5b.
  // Flagged = a signed market AND (a sign code, OR a market edit onto a signed market,
  // OR the server's 422 listed it). The market-edit source needs the original parse with
  // the same leg count, as the server's trusted source does (D2.2).
  {
    const legs = parsed.legs || [];
    const origLegs = originalParsed && Array.isArray(originalParsed.legs) && originalParsed.legs.length === legs.length
      ? originalParsed.legs : null;
    const failedSet = new Set(Array.isArray(failedLegIndexes) ? failedLegIndexes : []);
    legs.forEach((l, idx) => {
      if (!l || typeof l !== 'object') return;
      const league = l.league ?? parsed.league ?? 'nhl';
      if (!clientSigned(l, league)) return;
      const line = effectiveSpreadLine(l);
      const flagged = hasSpreadSignFlag(l)
        || (origLegs != null && isMarketEditFlag({ originalCanonical: origLegs[idx] && origLegs[idx].canonicalMarket, nowSigned: true, line }))
        || failedSet.has(idx);
      if (!flagged) return;
      spreadSignLegIndices.push(idx);
      const state = spreadSignState(line, l.lineSign);
      if (state === 'ok') return;
      const msg = spreadSignMessage(l, state);
      legMessages[idx] = msg;
      if (!hardBlockMessages.includes(msg)) hardBlockMessages.push(msg);
      affectedLegIndexSet.add(idx);
    });
  }

  // ── SOFT STOPS (save permitted with inline warning) ────────────────

  const wc = parsed.warningCodes || [];
  const legs = parsed.legs || [];

  // 3. INVALID_LINE_FOR_MARKET
  if (wc.includes('INVALID_LINE_FOR_MARKET')) {
    softWarnMessages.push('One or more legs has an invalid line — review before saving.');
    (parsed.legScores || []).forEach(s => {
      if ((s.codes || []).includes('INVALID_LINE_FOR_MARKET')) affectedLegIndexSet.add(s.legIndex);
    });
  }

  // 4. INVALID_LINE_FOR_BINARY_MARKET
  if (wc.includes('INVALID_LINE_FOR_BINARY_MARKET')) {
    softWarnMessages.push('One or more legs has a conflicting line — review before saving.');
    (parsed.legScores || []).forEach(s => {
      if ((s.codes || []).includes('INVALID_LINE_FOR_BINARY_MARKET')) affectedLegIndexSet.add(s.legIndex);
    });
  }

  // 5. PICK_NOT_IN_GAME on team market.
  // Read canonicalMarket || type || market, normalize through displayMarketKey()
  // (maps prefixed -> short-form via LEGACY_MARKET_ALIAS) then check via
  // isEnrichablePlayerProp(). Player-prop legs never generate
  // a genuine PICK_NOT_IN_GAME (no team pick) so they must not warn here.
  // Null/undefined market normalizes to '' which is not in the set -> conservative warn.
  if (wc.includes('PICK_NOT_IN_GAME') && legs.some(l => {
    if (!(l.resolution_warnings || []).includes('PICK_NOT_IN_GAME')) return false;
    const shortKey = displayMarketKey({
      canonicalMarket: l.canonicalMarket,
      type: l.type,
      market: l.market,
    });
    const league = l.league ?? parsed.league ?? 'nhl';
    return !isEnrichablePlayerProp(shortKey, league);
  })) {
    softWarnMessages.push('Picked team does not match this game — review before saving.');
    legs.forEach((l, idx) => {
      if (!(l.resolution_warnings || []).includes('PICK_NOT_IN_GAME')) return;
      const shortKey = displayMarketKey({
        canonicalMarket: l.canonicalMarket,
        type: l.type,
        market: l.market,
      });
      const league = l.league ?? parsed.league ?? 'nhl';
      if (!isEnrichablePlayerProp(shortKey, league)) affectedLegIndexSet.add(idx);
    });
  }

  // 5b (#1040 PR D0 soft spread-sign stop) is replaced by hard rule 2d (#1132).

  // Sections 6, 7, 8 (single/parlay/sgp+/sgp missing-gameId soft warnings, including
  // the SGP silent-allow for fully-enrichable player-prop legs) were removed in #1166
  // D2.4 -- rule 2c above now hard-blocks every game-less leg regardless of slip type
  // or enrichability, since import-confirm's #907 gate rejects the whole import (422)
  // for any leg with no gameId. The soft "will retry automatically after save" promise
  // was false: the client never retries a rejected save.

  const affectedLegIndices = [...affectedLegIndexSet].sort((a, b) => a - b);

  return {
    canSave: hardBlockMessages.length === 0,
    hardBlockMessages,
    softWarnMessages,
    affectedLegIndices,
    messages: hardBlockMessages, // backward compat
    spreadSignLegIndices,
    legMessages,
  };
}
